import type { CharacterProfile, ChatExportFields, WorldDocument } from "@shared/schema";
import type { APIKeyConfig, ValidationResult } from "@/types/api-key";
import {
  BaseProvider,
  type ProviderResponse,
  type ImagePlanPrompts,
  type TextGenerationOptions,
  type ImageGenerationOptions,
  type RoleplayOptions,
  type EditOptions,
} from "./provider-interface";
import { parseOpenAIUsage } from "./usage-helpers";

/**
 * OpenAI Provider Implementation
 *
 * Supports:
 * - Text generation (GPT-4o, GPT-5, etc.)
 * - Image generation (DALL-E 3, GPT-Image-1)
 * - Image editing
 * - Web search (for supported models)
 */
export class OpenAIProvider extends BaseProvider {
  // Validation
  async validateApiKey(config: {
    apiKey: string;
    model?: string;
  }): Promise<ValidationResult> {
    try {
      // First try GET /v1/models
      await this.getModels(config.apiKey);
      return { isValid: true };
    } catch (primaryError: any) {
      // If that fails, try model-specific validation
      try {
        const model = config.model || 'gpt-4o';
        const isG5 = this.isGpt5Model(model);
        const isImageModel = /^(dall-e-3|gpt-image-1(\.5)?)$/i.test(model);

        if (isG5) {
          await this.postResponses(config.apiKey, {
            model,
            input: 'Validation ping.',
            max_output_tokens: 16,
          });
          return { isValid: true };
        } else if (isImageModel) {
          const payload: any = {
            model,
            prompt: 'Validation image ping',
            n: 1,
            size: '1024x1024',
          };
          if (!this.isGptImageModel(model)) {
            payload.response_format = 'b64_json';
          }
          await this.postImageGenerations(config.apiKey, payload);
          return { isValid: true };
        }

        await this.postChatCompletions(config.apiKey, {
          model,
          messages: [{ role: 'user', content: 'Validation ping.' }],
          max_tokens: 4,
        });
        return { isValid: true };
      } catch (fallbackError: any) {
        const msg = String(fallbackError?.message || primaryError?.message || 'Validation failed');
        return { isValid: false, error: msg };
      }
    }
  }

  // Character Profile Generation
  async generateCharacterProfile(
    prompts: { system: string; user: string },
    config: APIKeyConfig,
    worldDocument?: WorldDocument,
    opts?: TextGenerationOptions
  ): Promise<ProviderResponse<CharacterProfile>> {
    this.validateConfig(config);
    this.checkAborted(opts?.signal);

    const payload: any = {
      model: config.model,
      messages: [
        { role: 'system', content: prompts.system },
        { role: 'user', content: prompts.user },
      ],
    };

    if (!this.isGpt5Model(config.model)) {
      payload.temperature = config.temperature ?? 0.7;
    }

    if (this.shouldEnableWebSearch(config)) {
      payload.tools = [{ type: 'web_search' }];
    }

    const json = await this.postChatCompletions(config.apiKey, payload, { signal: opts?.signal });
    const usage = parseOpenAIUsage(json);
    const content = String(json?.choices?.[0]?.message?.content ?? '').trim();
    const parsed = this.parseJsonSafe<any>(content);
    const data = parsed?.character_profile || parsed?.character || parsed;
    return { value: data as CharacterProfile, usage };
  }

  // Chat Export Fields
  async generateChatExportFields(
    prompts: { system: string; user: string },
    config: APIKeyConfig,
    opts?: TextGenerationOptions
  ): Promise<ProviderResponse<ChatExportFields>> {
    this.validateConfig(config);
    this.checkAborted(opts?.signal);

    const payload: any = {
      model: config.model,
      messages: [
        { role: 'system', content: prompts.system },
        { role: 'user', content: prompts.user },
      ],
    };

    if (!this.isGpt5Model(config.model)) {
      payload.temperature = config.temperature ?? 0.7;
    }

    if (this.shouldEnableWebSearch(config)) {
      payload.tools = [{ type: 'web_search' }];
    }

    const json = await this.postChatCompletions(config.apiKey, payload, { signal: opts?.signal });
    const usage = parseOpenAIUsage(json);
    const content = String(json?.choices?.[0]?.message?.content ?? '').trim();
    return { value: this.parseJsonSafe<ChatExportFields>(content), usage };
  }

  // Image Plan Prompts
  async generateImagePlanPrompts(
    prompts: { system: string; user: string },
    config: APIKeyConfig,
    opts?: TextGenerationOptions
  ): Promise<ProviderResponse<ImagePlanPrompts>> {
    this.validateConfig(config);
    this.checkAborted(opts?.signal);

    const payload: any = {
      model: config.model,
      messages: [
        { role: 'system', content: prompts.system },
        { role: 'user', content: prompts.user },
      ],
    };

    if (!this.isGpt5Model(config.model)) {
      payload.temperature = config.temperature ?? 0.7;
    }

    if (this.shouldEnableWebSearch(config)) {
      payload.tools = [{ type: 'web_search' }];
    }

    const json = await this.postChatCompletions(config.apiKey, payload, { signal: opts?.signal });
    const usage = parseOpenAIUsage(json);
    const content = String(json?.choices?.[0]?.message?.content ?? '').trim();
    const value = this.parseJsonSafe<ImagePlanPrompts>(content);
    return { value, usage };
  }

  // Field Rewrite
  async rewriteCharacterField(
    instructions: string,
    config: APIKeyConfig,
    opts?: TextGenerationOptions
  ): Promise<ProviderResponse<string>> {
    this.validateConfig(config);
    this.checkAborted(opts?.signal);

    const payload: any = {
      model: config.model,
      messages: [{ role: 'user', content: instructions }],
    };

    if (!this.isGpt5Model(config.model)) {
      payload.temperature = config.temperature ?? 0.7;
    }

    if (this.shouldEnableWebSearch(config)) {
      payload.tools = [{ type: 'web_search' }];
    }

    const json = await this.postChatCompletions(config.apiKey, payload, { signal: opts?.signal });
    const usage = parseOpenAIUsage(json);
    const content = String(json?.choices?.[0]?.message?.content ?? '').trim();

    // Strip code block markers if present
    const codeBlock = content.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/);
    const value = (codeBlock ? codeBlock[1] : content).trim();
    return { value, usage };
  }

  // AI Edit
  async editCharacter(
    prompts: { system: string; user: string },
    config: APIKeyConfig,
    opts?: EditOptions
  ): Promise<ProviderResponse<any>> {
    this.validateConfig(config);
    this.checkAborted(opts?.signal);

    const payload: any = {
      model: config.model,
      messages: [
        { role: 'system', content: prompts.system },
        { role: 'user', content: prompts.user },
      ],
    };

    if (!this.isGpt5Model(config.model)) {
      payload.temperature = config.temperature ?? 0.7;
    }

    if (this.shouldEnableWebSearch(config)) {
      payload.tools = [{ type: 'web_search' }];
    }

    const json = await this.postChatCompletions(config.apiKey, payload, { signal: opts?.signal });
    const usage = parseOpenAIUsage(json);
    const content = String(json?.choices?.[0]?.message?.content ?? '').trim();
    const parsed = this.parseJsonSafe<any>(content);
    return { value: parsed, usage };
  }

  // Roleplay Reply
  async generateRoleplayReply(
    params: {
      system: string;
      messages: Array<{ role: 'user' | 'assistant'; content: string }>;
    },
    config: APIKeyConfig,
    opts?: RoleplayOptions
  ): Promise<ProviderResponse<string>> {
    this.validateConfig(config);
    this.checkAborted(opts?.signal);

    const payload: any = {
      model: config.model,
      messages: [
        { role: 'system', content: params.system },
        ...params.messages.map((m) => ({ role: m.role, content: m.content })),
      ],
    };

    if (!this.isGpt5Model(config.model)) {
      payload.temperature = config.temperature ?? 0.7;
    }

    if (this.shouldEnableWebSearch(config)) {
      payload.tools = [{ type: 'web_search' }];
    }

    const json = await this.postChatCompletions(config.apiKey, payload, { signal: opts?.signal });
    const usage = parseOpenAIUsage(json);
    const content = String(json?.choices?.[0]?.message?.content ?? '').trim();
    return { value: content, usage };
  }

  // Image Generation
  async generateImage(
    prompt: string,
    config: APIKeyConfig,
    opts?: ImageGenerationOptions
  ): Promise<ProviderResponse<{ imageData: string }>> {
    this.validateConfig(config);
    this.checkAborted(opts?.signal);

    const isGptImg = this.isGptImageModel(config.model);
    const payload: any = {
      model: config.model,
      prompt,
      n: 1,
      size: '1024x1024',
    };

    // For dall-e-3 request base64; for gpt-image-1, omit unsupported response_format
    if (!isGptImg) {
      payload.response_format = 'b64_json';
    }

    const json = await this.postImageGenerations(config.apiKey, payload, { signal: opts?.signal });
    const usage = parseOpenAIUsage(json);
    const b64 = (json as any)?.data?.[0]?.b64_json;

    if (typeof b64 === 'string' && b64.length > 100) {
      return { value: { imageData: `data:image/png;base64,${b64}` }, usage };
    }

    // Some responses might embed markdown image with data URI inside choices
    const choiceContent = String((json as any)?.choices?.[0]?.message?.content || '').trim();
    const dataUriMatch = choiceContent.match(/data:image\/(png|jpe?g|webp);base64,[A-Za-z0-9+/=]+/i);
    if (dataUriMatch) {
      return { value: { imageData: dataUriMatch[0] }, usage };
    }

    throw new Error('No image data returned by OpenAI');
  }

  // Image Edit
  async generateImageEdit(
    referenceImage: string,
    prompt: string,
    config: APIKeyConfig,
    opts?: ImageGenerationOptions
  ): Promise<ProviderResponse<{ imageData: string }>> {
    this.validateConfig(config);
    this.checkAborted(opts?.signal);

    const isDataUri = /^data:image\//i.test(referenceImage);
    const isHttpUrl = /^https?:\/\//i.test(referenceImage);
    let resolvedBlob: Blob;

    if (isDataUri) {
      resolvedBlob = this.dataUriToBlob(referenceImage);
    } else if (isHttpUrl) {
      // Attempt to inline via server proxy to avoid CORS; fall back to direct fetch if proxy fails
      try {
        const dataUri = await this.inlineImageViaProxy(referenceImage, opts?.signal);
        resolvedBlob = this.dataUriToBlob(dataUri);
      } catch {
        resolvedBlob = await this.urlToBlob(referenceImage, opts?.signal);
      }
    } else {
      throw new Error('Unsupported reference image format');
    }

    const fileName = this.generateFileName(referenceImage, isHttpUrl, isDataUri);
    const isGptImg = this.isGptImageModel(config.model);
    const form = new FormData();
    form.append('model', config.model);
    form.append('prompt', prompt);
    form.append('image', resolvedBlob, fileName);
    form.append('size', '1024x1024');

    // For DALL-E 3 request base64 JSON; for gpt-image-1 omit to avoid unsupported param
    if (!isGptImg) form.append('response_format', 'b64_json');

    const json = await this.postImageEdits(config.apiKey, form, { signal: opts?.signal });
    const usage = parseOpenAIUsage(json);
    const b64 = (json as any)?.data?.[0]?.b64_json;
    const url = (json as any)?.data?.[0]?.url;

    if (typeof b64 === 'string' && b64.length > 100) {
      return { value: { imageData: `data:image/png;base64,${b64}` }, usage };
    }

    if (typeof url === 'string' && url) {
      // Fetch URL to data URI for consistent downstream handling
      const resp = await fetch(url, { signal: opts?.signal });
      if (!resp.ok) throw new Error(`OpenAI returned image URL fetch failed: HTTP ${resp.status}`);
      const blob = await resp.blob();
      const dataUri = await this.blobToDataUri(blob);
      return { value: { imageData: dataUri }, usage };
    }

    throw new Error('No image data returned by OpenAI edits');
  }

  // Private helper methods

  private isGpt5Model(model: string): boolean {
    return /^gpt-5/i.test(model || '');
  }

  private isGptImageModel(model: string): boolean {
    return /^gpt-image-1(\.5)?$/i.test(model || '');
  }

  private supportsWebSearch(model: string): boolean {
    const normalized = String(model || '').toLowerCase();
    return (
      normalized.startsWith('gpt-4.1') ||
      normalized.startsWith('gpt-4o') ||
      normalized.startsWith('gpt-5')
    );
  }

  private shouldEnableWebSearch(config: APIKeyConfig): boolean {
    return Boolean(config.enableWebSearch) && this.supportsWebSearch(config.model);
  }

  private extractJsonObject(text: string): string {
    let t = (text || '').trim();
    const codeBlock = t.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/);
    if (codeBlock) t = codeBlock[1].trim();
    const jsonMatch = t.match(/(\{[\s\S]*\})/);
    if (jsonMatch) t = jsonMatch[1];
    return t;
  }

  private parseJsonSafe<T>(text: string): T {
    const cleaned = this.extractJsonObject(text);
    return JSON.parse(cleaned) as T;
  }

  private async getModels(apiKey: string, opts?: { signal?: AbortSignal }): Promise<any> {
    const resp = await fetch('https://api.openai.com/v1/models', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${apiKey}`,
      },
      signal: opts?.signal,
    });
    if (!resp.ok) {
      let msg = `HTTP ${resp.status}`;
      try {
        const err = await resp.json();
        msg = err?.error?.message || err?.message || msg;
      } catch {}
      throw new Error(msg);
    }
    return resp.json();
  }

  private async postResponses(apiKey: string, payload: any, opts?: { signal?: AbortSignal }): Promise<any> {
    const resp = await fetch('https://api.openai.com/v1/responses', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify(payload),
      signal: opts?.signal,
    });
    if (!resp.ok) {
      let msg = `HTTP ${resp.status}`;
      try {
        const err = await resp.json();
        msg = err?.error?.message || err?.message || msg;
      } catch {}
      throw new Error(msg);
    }
    return resp.json();
  }

  private async postChatCompletions(apiKey: string, payload: any, opts?: { signal?: AbortSignal }): Promise<any> {
    const resp = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify(payload),
      signal: opts?.signal,
    });
    if (!resp.ok) {
      let msg = `HTTP ${resp.status}`;
      try {
        const err = await resp.json();
        msg = err?.error?.message || err?.message || msg;
      } catch {}
      throw new Error(msg);
    }
    return resp.json();
  }

  private async postImageGenerations(apiKey: string, payload: any, opts?: { signal?: AbortSignal }): Promise<any> {
    const resp = await fetch('https://api.openai.com/v1/images/generations', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify(payload),
      signal: opts?.signal,
    });
    if (!resp.ok) {
      let msg = `HTTP ${resp.status}`;
      try {
        const err = await resp.json();
        msg = err?.error?.message || err?.message || msg;
      } catch {}
      throw new Error(msg);
    }
    return resp.json();
  }

  private async postImageEdits(apiKey: string, form: FormData, opts?: { signal?: AbortSignal }): Promise<any> {
    const resp = await fetch('https://api.openai.com/v1/images/edits', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${apiKey}`,
        // Do not set Content-Type; browser will set proper multipart boundary
      } as any,
      body: form,
      signal: opts?.signal,
    });
    if (!resp.ok) {
      let msg = `HTTP ${resp.status}`;
      try {
        const err = await resp.json();
        msg = err?.error?.message || err?.message || msg;
      } catch {}
      throw new Error(msg);
    }
    return resp.json();
  }

  private dataUriToBlob(dataUri: string): Blob {
    const m = dataUri.match(/^data:([^;]+);base64,(.+)$/);
    if (!m) throw new Error('Invalid data URI for image');
    const mime = m[1];
    const b64 = m[2].replace(/\s+/g, '');
    const bin = atob(b64);
    const len = bin.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  }

  private async urlToBlob(url: string, signal?: AbortSignal): Promise<Blob> {
    const resp = await fetch(url, { signal });
    if (!resp.ok) throw new Error(`Failed to fetch reference image: HTTP ${resp.status}`);
    return resp.blob();
  }

  private async blobToDataUri(blob: Blob): Promise<string> {
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }

  private async inlineImageViaProxy(url: string, signal?: AbortSignal): Promise<string> {
    const resp = await fetch('/pyapi/v1/media/proxy-image', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
      credentials: 'include',
      signal,
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      const detail = data?.detail || `HTTP ${resp.status}`;
      throw new Error(`Proxy image failed: ${detail}`);
    }
    const dataUri = String(data?.dataUri || '');
    if (!dataUri.startsWith('data:image/')) throw new Error('Proxy response missing dataUri');
    return dataUri;
  }

  private generateFileName(referenceImage: string, isHttpUrl: boolean, isDataUri: boolean): string {
    if (isHttpUrl) {
      try {
        const url = new URL(referenceImage);
        const parts = url.pathname.split('/').filter(Boolean);
        const last = parts[parts.length - 1];
        if (last) return last;
      } catch {}
    }
    if (isDataUri) {
      const match = referenceImage.match(/^data:[^/]+\/([^;]+);/);
      if (match?.[1]) {
        return `reference.${match[1].toLowerCase().replace(/[^a-z0-9]+/g, '') || 'png'}`;
      }
    }
    return 'reference.png';
  }
}
